import { Mat4, Vec3 } from 'playcanvas';

import { ElementType } from '../element';
import type { Scene } from '../scene';
import type { Splat } from '../splat';
import { State } from '../splat-state';

export type BoundaryPlane = {
    /** Unit normal (world space) */
    nx: number;
    ny: number;
    nz: number;
    /** Plane: nx*x + ny*y + nz*z + d = 0 */
    d: number;
};

const tmp = new Vec3();
const tmpW = new Vec3();
const mat = new Mat4();

/** Newell normal for a closed 3D polygon (robust for non-planar verts). */
export function computePolygonNormalNewell(vertices: Vec3[]): Vec3 {
    const n = vertices.length;
    const out = new Vec3(0, 0, 0);
    if (n < 3) return out;
    for (let i = 0; i < n; i++) {
        const a = vertices[i];
        const b = vertices[(i + 1) % n];
        out.x += (a.y - b.y) * (a.z + b.z);
        out.y += (a.z - b.z) * (a.x + b.x);
        out.z += (a.x - b.x) * (a.y + b.y);
    }
    if (out.lengthSq() < 1e-18) {
        out.set(0, 1, 0);
        return out;
    }
    out.normalize();
    return out;
}

/** Centroid of vertices. */
export function centroidVectors(vertices: Vec3[]): Vec3 {
    const c = new Vec3(0, 0, 0);
    if (!vertices.length) return c;
    for (const v of vertices) {
        c.add(v);
    }
    c.mulScalar(1 / vertices.length);
    return c;
}

/**
 * Best-fit base plane through polygon vertices (weighted least-squares via centroid + plane normal).
 * Normal from Newell's method (robust for mildly non-planar closed loops); offsets so plane passes centroid.
 */
export function computeBestFitPlane(vertices: Vec3[]): BoundaryPlane {
    if (vertices.length < 3) {
        return { nx: 0, ny: 1, nz: 0, d: 0 };
    }
    const c = centroidVectors(vertices);
    const nn = computePolygonNormalNewell(vertices);
    let fnx = nn.x;
    let fny = nn.y;
    let fnz = nn.z;
    const nlen = Math.hypot(fnx, fny, fnz);
    if (nlen < 1e-12) {
        return { nx: 0, ny: 1, nz: 0, d: -c.y };
    }
    fnx /= nlen;
    fny /= nlen;
    fnz /= nlen;
    const d = -(fnx * c.x + fny * c.y + fnz * c.z);
    return { nx: fnx, ny: fny, nz: fnz, d };
}

export function signedPlaneDistance(p: Vec3, plane: BoundaryPlane): number {
    return plane.nx * p.x + plane.ny * p.y + plane.nz * p.z + plane.d;
}

/** Shoelace area of polygon projected onto XZ (y-up world). */
export function polygonAreaXZ(vertices: Vec3[]): number {
    const m = vertices.length;
    if (m < 3) return 0;
    let sum = 0;
    for (let i = 0; i < m; i++) {
        const a = vertices[i];
        const b = vertices[(i + 1) % m];
        sum += a.x * b.z - b.x * a.z;
    }
    return Math.abs(sum) * 0.5;
}

function pointInPolygonXZ(x: number, z: number, poly: Vec3[]): boolean {
    let inside = false;
    for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
        const xi = poly[i].x;
        const zi = poly[i].z;
        const xj = poly[j].x;
        const zj = poly[j].z;
        const intersect =
            zi > z !== zj > z &&
            x < ((xj - xi) * (z - zi)) / (zj - zi + 1e-30) + xi;
        if (intersect) inside = !inside;
    }
    return inside;
}

const GRID = 256;

/**
 * Volume (m³) above best-fit base plane inside polygon footprint (XZ), sampling splat centers.
 */
export function computeStockpileVolume(scene: Scene, footprintWorld: Vec3[], plane: BoundaryPlane): number {
    if (footprintWorld.length < 3) return 0;

    let minX = Infinity;
    let maxX = -Infinity;
    let minZ = Infinity;
    let maxZ = -Infinity;
    for (const v of footprintWorld) {
        minX = Math.min(minX, v.x);
        maxX = Math.max(maxX, v.x);
        minZ = Math.min(minZ, v.z);
        maxZ = Math.max(maxZ, v.z);
    }
    const dx = (maxX - minX) / GRID || 1;
    const dz = (maxZ - minZ) / GRID || 1;
    const cellArea = dx * dz;

    const heightGrid = new Float32Array(GRID * GRID);
    heightGrid.fill(-Infinity);

    const splats = scene.getElementsByType(ElementType.splat) as Splat[];
    for (const splat of splats) {
        const splatData = splat.splatData;
        const xs = splatData.getProp('x') as Float32Array;
        const ys = splatData.getProp('y') as Float32Array;
        const zs = splatData.getProp('z') as Float32Array;
        const state = splatData.getProp('state') as Uint8Array | undefined;
        const n = splat.numSplats;
        mat.copy(splat.worldTransform);
        for (let i = 0; i < n; i++) {
            if (state && (state[i] & State.deleted)) continue;
            tmp.set(xs[i], ys[i], zs[i]);
            mat.transformPoint(tmp, tmpW);
            const ix = Math.floor(((tmpW.x - minX) / (maxX - minX + 1e-12)) * GRID);
            const iz = Math.floor(((tmpW.z - minZ) / (maxZ - minZ + 1e-12)) * GRID);
            if (ix < 0 || ix >= GRID || iz < 0 || iz >= GRID) continue;
            const idx = iz * GRID + ix;
            if (tmpW.y > heightGrid[idx]) heightGrid[idx] = tmpW.y;
        }
    }

    let vol = 0;
    for (let iz = 0; iz < GRID; iz++) {
        for (let ix = 0; ix < GRID; ix++) {
            const cx = minX + (ix + 0.5) * dx;
            const cz = minZ + (iz + 0.5) * dz;
            if (!pointInPolygonXZ(cx, cz, footprintWorld)) continue;
            const h = heightGrid[iz * GRID + ix];
            if (!Number.isFinite(h)) continue;
            tmp.set(cx, h, cz);
            const dist = signedPlaneDistance(tmp, plane);
            if (dist > 0) vol += dist * cellArea;
        }
    }
    return vol;
}
